import { FC, useState } from 'react'
import { Box } from '@/components/atoms/Grid'
import Link from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import styled from 'styled-components'
import { SlugPath } from 'types/slugPath'
import { Category } from '@/network/graphql.g'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { ID } from 'graphql-ws'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { Body } from '@/components/atoms/typography'

type ContentCategoryMenuRedesignProps = {
  subcategories: Category[]
  slugPath: SlugPath
} & Rest

const filterVisible = (items: Category[]) =>
  items?.filter(({ visibleInNavigation }) => visibleInNavigation) || []

const findCategoryById = (
  categories: Category[],
  targetId: ID
): Category | null => {
  for (const cat of categories) {
    if (cat.id === targetId) {
      return cat
    }
    if (cat.children?.length) {
      const found = findCategoryById(cat.children, targetId)
      if (found) {
        return found
      }
    }
  }
  return null
}

const hasChildren = (category: Category) =>
  category?.children && category.children.length > 0

const isCategoryOrChildrenSelected = (
  category: Category,
  pathIds: ID[]
): boolean => {
  if (pathIds.includes(category.id)) {
    return true
  }
  if (category.children) {
    for (const child of category.children) {
      if (isCategoryOrChildrenSelected(child, pathIds)) {
        return true
      }
    }
  }
  return false
}

const CategoryItem: FC<{
  category: Category
  level?: number
  isFilter?: boolean
  router: NextRouter
  expandedIds: ID[]
  setExpandedIds: (ids: ID[]) => void
  slugPath: SlugPath
}> = ({
  category,
  level = 0,
  isFilter = false,
  router,
  expandedIds,
  setExpandedIds,
  slugPath
}) => {
  const isOpen = expandedIds.includes(category.id)
  const hasChildrenFlag = hasChildren(category)

  const pathIds = [
    slugPath?.categoryId?.id,
    slugPath?.subcategoryId?.id,
    slugPath?.itemId?.id,
    slugPath?.subItemId?.id
  ].filter(Boolean)

  const isSelected = pathIds.includes(category.id)

  const query =
    isFilter && router
      ? convertQueryParamsAndRemoveEmptyGetParameters({
          query: router.query,
          allowOnly: [
            FilterQueryItem.STOCK,
            FilterQueryItem.MAIN_WAREHOUSE,
            FilterQueryItem.NEWS,
            FilterQueryItem.SIZE,
            FilterQueryItem.COLOR,
            FilterQueryItem.MATERIAL,
            FilterQueryItem.MANUFACTURER,
            FilterQueryItem.PRICE_MIN,
            FilterQueryItem.PRICE_MAX
          ]
        })
      : null

  const isParent = level === 0

  const Wrapper = isParent ? ParentCategoryWrapper : Box

  return (
    <Wrapper ml={level > 0 ? 10 : 0}>
      <StyledLink
        href={`${category.url}${query ? `?${query}` : ''}`}
        data-testid="categoryLink"
      >
        <StyledBody isSelected={isSelected} isParent={isParent}>
          {category.title}
        </StyledBody>
      </StyledLink>

      {isOpen && hasChildrenFlag && (
        <ChildrenWrapper>
          {filterVisible(category.children).map((child) => (
            <CategoryItem
              key={child.id}
              category={child}
              level={level + 1}
              isFilter={isFilter}
              router={router}
              expandedIds={expandedIds}
              setExpandedIds={setExpandedIds}
              slugPath={slugPath}
            />
          ))}
        </ChildrenWrapper>
      )}
    </Wrapper>
  )
}

const ContentCategoryMenuRedesign: FC<ContentCategoryMenuRedesignProps> = ({
  subcategories,
  slugPath,
  ...rest
}) => {
  const router = useRouter()

  const data = filterVisible(subcategories ?? [])

  const getInitialExpandedIds = () => {
    const ids: ID[] = []
    const pathLevels = [
      { key: 'itemId', value: slugPath?.itemId },
      { key: 'subcategoryId', value: slugPath?.subcategoryId },
      { key: 'categoryId', value: slugPath?.categoryId }
    ]

    for (const { value } of pathLevels) {
      if (value?.id) {
        const category = findCategoryById(data, value.id)
        if (hasChildren(category) || slugPath?.subItemId) {
          ids.push(value.id)
        }
      }
    }
    return ids
  }

  const [expandedIds, setExpandedIds] = useState<ID[]>(getInitialExpandedIds)

  const pathIds = [
    slugPath?.categoryId?.id,
    slugPath?.subcategoryId?.id,
    slugPath?.itemId?.id,
    slugPath?.subItemId?.id
  ].filter(Boolean)

  const isFilter =
    expandedIds.length > 0 ||
    data.some((category) => isCategoryOrChildrenSelected(category, pathIds))

  return (
    <SidebarWrapper {...rest} data-testid="contentCategoryMenuDesktop">
      {data.map((category) => (
        <CategoryItem
          key={category.id}
          category={category}
          isFilter={isFilter}
          router={router}
          expandedIds={expandedIds}
          setExpandedIds={setExpandedIds}
          slugPath={slugPath}
        />
      ))}
    </SidebarWrapper>
  )
}

export default ContentCategoryMenuRedesign

const SidebarWrapper = styled(Box)`
  position: sticky;
  top: 150px;
  align-self: flex-start;
  height: fit-content;
`

const StyledBody = styled<{ isSelected?: boolean }>(Body)`
  font-weight: ${(props) => (props.isSelected ? 700 : 400)};
  font-style: normal;
  font-size: ${(props) => (props.isParent ? '16px' : '14px')};
  line-height: 100%;
  letter-spacing: -0.01em;
  opacity: ${(props) => (props.isSelected ? 1 : 0.8)};
  margin: 0;
  &:hover {
    opacity: 1;
    cursor: pointer;
  }
`

const ParentCategoryWrapper = styled(Box)`
  padding-bottom: 16px;
`

const ChildrenWrapper = styled(Box)`
  gap: 10px;
  padding: 12px 0 8px 8px;
  opacity: 1;
  display: flex;
  flex-direction: column;
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
`
